import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import validate from 'validate.js';

import { get } from 'dpl/shared/utils/object';
import { normalizeFormValue, isEqualFormValues } from 'dpl/shared/form/utils';
import QuizChildrenAgesQuestion from 'dpl/adoption_application/components/QuizChildrenAgesQuestion';
import QuizChildrenCountQuestion from 'dpl/adoption_application/components/QuizChildrenCountQuestion';
import QuizCurrentPetsTypesQuestion from 'dpl/adoption_application/components/QuizCurrentPetsTypesQuestion';
import QuizHasChildrenQuestion from 'dpl/adoption_application/components/QuizHasChildrenQuestion';
import QuizHomeTypeQuestion from 'dpl/adoption_application/components/QuizHomeTypeQuestion';
import QuizLifestyleQuestion from 'dpl/adoption_application/components/QuizLifestyleQuestion';
import QuizPastDogsQuestion from 'dpl/adoption_application/components/QuizPastDogsQuestion';
import QuizSizeQuestionContainer from 'dpl/adoption_application/containers/QuizSizeQuestionContainer';
import QuizColorQuestionContainer from 'dpl/adoption_application/containers/QuizColorQuestionContainer';
import QuizPrimaryCaretakerQuestion from 'dpl/adoption_application/components/QuizPrimaryCaretakerQuestion';
import QuizPrimaryCaretakerDescriptionQuestion from 'dpl/adoption_application/components/QuizPrimaryCaretakerDescriptionQuestion';
import QuizGenderPreferenceQuestion from 'dpl/adoption_application/components/QuizGenderPreferenceQuestion';
import QuizProfilePhotoQuestion from 'dpl/adoption_application/components/QuizProfilePhotoQuestion';
import QuizBreederBreedQuestion from 'dpl/adoption_application/components/QuizBreederBreedQuestion';
import QuizPuppyQuestion from 'dpl/adoption_application/components/QuizPuppyQuestion';
import QuizLitterQuestion from 'dpl/adoption_application/components/QuizLitterQuestion';
import QuizCustomQuestionInterstitial from 'dpl/adoption_application/components/QuizCustomQuestionInterstitial';
import QuizCustomQuestion from 'dpl/adoption_application/components/QuizCustomQuestion';
import QuizDeliveryMethodQuestionContainer from 'dpl/adoption_application/containers/QuizDeliveryMethodQuestionContainer';
import QuizGoodDogPayPreferenceQuestion from 'dpl/adoption_application/components/QuizGoodDogPayPreferenceQuestion';
import QuizNameQuestion from 'dpl/adoption_application/components/QuizNameQuestion';
import QuizPhoneNumberQuestion from 'dpl/adoption_application/components/QuizPhoneNumberQuestion';
import QuizEmailQuestion from 'dpl/adoption_application/components/QuizEmailQuestion';
import QuizOverEighteenQuestion from 'dpl/adoption_application/components/QuizOverEighteenQuestion';
import QuizPasswordQuestion from 'dpl/adoption_application/components/QuizPasswordQuestion';
import QuizZipcodeQuestion from 'dpl/adoption_application/components/QuizZipcodeQuestion';
import Quiz from 'dpl/adoption_application/components/Quiz';
import isFeatureEnabled from 'dpl/util/isFeatureEnabled';
import { FEATURES } from 'dpl/shared/constants/features';

const BASICS_SECTION = 'Basics';
const HOME_SECTION = 'Home & Puppy Care';
const PREFERENCES_SECTION = 'Preferences';

// updates to ordering here will also require updates
// to ordering in EditApplicationPageQuestionSection
function mapPropsToOrderedSteps(props) {
  const {
    addToFormState,
    availablePuppies,
    availableLitters,
    canOverrideAvailability,
    allUploadingUploads,
    breederName,
    programName,
    buyerApplicationData,
    formState,
    formErrors,
    handleAllUploadError,
    handleAllUploadSuccess,
    handleUploadInputChange,
    onSubmit,
    availableDeliveryMethods,
    pushToFormState,
    removeFromFormState,
    batchAddToFormState,
    availableBreeds,
    reservedBreeds,
    alreadyHasProfilePhoto,
    alreadyHasPhoneNumber,
    alreadyClaimedAccount,
    alreadyHasZipCode,
    alreadyHasEmail,
    alreadyOverEighteen,
    isFormValidating,
    resetFormState,
    currentUser
  } = props;

  const isBuyerAppAuthRemovalEnabled = isFeatureEnabled(
    FEATURES.RELEASE_BUYER_APPLICATION_AUTH_REMOVAL
  );

  const { custom_application_answers_attributes: customAnswers } =
    buyerApplicationData.value.buyer_application;

  const customQuestionSteps = customAnswers.map((caa, idx) => ({
    questionName: 'custom-question',
    questionTitle: `Breeder Q${idx}`,
    sectionName: PREFERENCES_SECTION,
    Component: QuizCustomQuestion,
    isValid: Boolean(
      get(formState, `custom_application_answers_attributes.${idx}.answer_text`)
    ),
    otherProps: {
      questionIdx: idx,
      subtitle: caa.question_text,
      title: caa.question_title
    }
  }));

  const chosenBreed =
    props.availableBreeds.find(b => b.id === formState.breed_id) || {};
  const hasOnlyOneUnsnoozedBreed = availableBreeds.length === 1;
  const hasOnlyOneUnsnoozedReservedBreed = reservedBreeds.length === 1;
  const availablePuppiesForChosenBreed = availablePuppies.filter(
    puppy => puppy.breed_id === formState.breed_id
  );
  const availableLittersForChosenBreed = availableLitters.filter(
    litter => litter.breed_id === formState.breed_id
  );
  const availablePreferences = chosenBreed.breeder_waitlist_preferences || [];
  const availableSizes = [];
  const availableColors = [];
  availablePreferences.forEach(ap => {
    if (ap.type === 'size') {
      availableSizes.push(ap);
    } else if (ap.type === 'color') {
      availableColors.push(ap);
    }
  });

  const puppyName = formState.dog_id
    ? availablePuppies.find(p => p.id === formState.dog_id)?.name || null
    : null;

  const preferenceQuestionSteps = [
    {
      questionName: 'litter-preference',
      questionTitle: 'Litter',
      sectionName: PREFERENCES_SECTION,
      Component: QuizLitterQuestion,
      isAnswered: Boolean(formState.litter_id),
      otherProps: {
        availableLitters: availableLittersForChosenBreed
      },
      onChange: addToFormState,
      shouldSkip:
        availableLittersForChosenBreed.length <= 1 ||
        (formState.dog_id && formState.litter_id)
    },
    availableSizes.length > 0 && {
      questionName: 'size-preference',
      questionTitle: 'Size',
      sectionName: PREFERENCES_SECTION,
      Component: QuizSizeQuestionContainer,
      isAnswered: Boolean(formState.size_preference_description),
      otherProps: {
        availableSizes,
        addToFormState,
        pushToFormState,
        removeFromFormState
      }
    },
    {
      questionName: 'gender-preference',
      questionTitle: 'Gender',
      sectionName: PREFERENCES_SECTION,
      Component: QuizGenderPreferenceQuestion,
      isValid: Boolean(formState.gender_preference),
      otherProps: {
        puppyName
      }
    },
    {
      questionName: 'color-preference',
      questionTitle: 'Color',
      sectionName: PREFERENCES_SECTION,
      Component: QuizColorQuestionContainer,
      isAnswered: Boolean(
        formState.color_preferences_attributes?.some(cpa => !cpa._destroy)
      ),
      otherProps: {
        availableColors,
        addToFormState,
        pushToFormState,
        removeFromFormState
      },
      shouldSkip: availableColors.length === 0
    }
  ].filter(Boolean);

  return [
    {
      questionName: 'name',
      questionTitle: 'Name',
      sectionName: BASICS_SECTION,
      Component: QuizNameQuestion,
      shouldSkip: !isBuyerAppAuthRemovalEnabled,
      isValid: ['first_name', 'last_name'].every(
        k =>
          !validate.validators.nonEmpty(
            get(formState, `buyer_profile_attributes.user_attributes.${k}`)
          )
      )
    },
    {
      questionName: 'email',
      questionTitle: 'Email',
      sectionName: BASICS_SECTION,
      Component: QuizEmailQuestion,
      shouldSkip:
        !isBuyerAppAuthRemovalEnabled ||
        (isBuyerAppAuthRemovalEnabled && alreadyHasEmail),
      isValid:
        Boolean(
          get(formState, 'buyer_profile_attributes.user_attributes.email')
        ) &&
        // need to wait for request to resolve
        !isFormValidating &&
        !formErrors['buyer_profile_attributes.user_attributes.email'],
      otherProps: { buyerApplicationData, resetFormState, currentUser }
    },
    {
      questionName: 'has-children',
      questionTitle: 'Kids',
      sectionName: BASICS_SECTION,
      Component: QuizHasChildrenQuestion,
      isValid: typeof normalizeFormValue(formState.has_children) === 'boolean'
    },
    {
      questionName: 'children-count',
      questionTitle: '# of Kids',
      sectionName: BASICS_SECTION,
      Component: QuizChildrenCountQuestion,
      shouldSkip: !normalizeFormValue(formState.has_children),
      isValid: Boolean(formState.children_count),
      onChange: value => {
        batchAddToFormState('children_count', value);
        batchAddToFormState('children_ages', []);
      }
    },
    {
      questionName: 'children-ages',
      questionTitle: 'Kid Ages',
      sectionName: BASICS_SECTION,
      Component: QuizChildrenAgesQuestion,
      shouldSkip:
        !normalizeFormValue(formState.has_children) ||
        !normalizeFormValue(formState.children_count),
      isValid: isEqualFormValues(
        formState.children_ages.filter(Boolean).length,
        parseInt(formState.children_count, 10)
      )
    },
    {
      questionName: 'current-pets',
      questionTitle: 'Pets',
      sectionName: BASICS_SECTION,
      Component: QuizCurrentPetsTypesQuestion,
      onChange: addToFormState,
      isValid: formState.current_pets_types.length > 0
    },
    {
      questionName: 'past-dogs',
      questionTitle: 'Dog History',
      sectionName: BASICS_SECTION,
      Component: QuizPastDogsQuestion,
      isValid: typeof normalizeFormValue(formState.has_past_dogs) === 'boolean'
    },
    {
      questionName: 'lifestyle',
      questionTitle: 'Applicant Bio',
      sectionName: BASICS_SECTION,
      Component: QuizLifestyleQuestion,
      otherProps: { breederName }
    },
    {
      questionName: 'home-type',
      questionTitle: 'Home Type',
      sectionName: HOME_SECTION,
      Component: QuizHomeTypeQuestion,
      isValid: Boolean(formState.home_type)
    },
    {
      questionName: 'caretaker',
      questionTitle: 'Caretaker',
      sectionName: HOME_SECTION,
      Component: QuizPrimaryCaretakerQuestion,
      isValid:
        typeof normalizeFormValue(formState.is_primary_caretaker) === 'boolean'
    },
    {
      questionName: 'caretaker-description',
      questionTitle: 'Caretaker Detail',
      sectionName: HOME_SECTION,
      Component: QuizPrimaryCaretakerDescriptionQuestion,
      shouldSkip: normalizeFormValue(formState.is_primary_caretaker),
      isValid: Boolean(formState.primary_caretaker_description)
    },
    {
      questionName: 'breed-preference',
      questionTitle: 'Breed',
      sectionName: PREFERENCES_SECTION,
      Component: QuizBreederBreedQuestion,
      shouldSkip:
        (hasOnlyOneUnsnoozedBreed && !canOverrideAvailability) ||
        (hasOnlyOneUnsnoozedReservedBreed && canOverrideAvailability),
      otherProps: {
        availableBreeds:
          availableBreeds.length === 0 && canOverrideAvailability
            ? reservedBreeds
            : availableBreeds
      },
      isValid: Boolean(formState.breed_id),
      onChange: addToFormState
    },
    {
      questionName: 'puppy-preference',
      questionTitle: 'Puppy',
      sectionName: PREFERENCES_SECTION,
      Component: QuizPuppyQuestion,
      shouldSkip: availablePuppiesForChosenBreed.length === 0,
      otherProps: {
        availablePuppies: availablePuppiesForChosenBreed,
        programName
      },
      isAnswered: Boolean(formState.dog_id),
      onChange: dog => {
        if (dog) {
          batchAddToFormState('dog_id', dog.id);
          batchAddToFormState('litter_id', dog.litter_id);
        } else {
          batchAddToFormState('dog_id', null);
          batchAddToFormState('litter_id', null);
        }
      }
    },

    ...preferenceQuestionSteps,

    {
      questionName: 'transportation-preference',
      questionTitle: 'Travel',
      sectionName: PREFERENCES_SECTION,
      Component: QuizDeliveryMethodQuestionContainer,
      onChange: addToFormState,
      isValid:
        [
          'can_pickup',
          'can_pickup_at_airport',
          'can_discuss_transportation'
        ].some(dmp => normalizeFormValue(formState[dmp])) ||
        (formState.application_delivery_preferences_attributes || []).length >
          0,
      otherProps: {
        addToFormState,
        availableDeliveryMethods,
        pushToFormState,
        removeFromFormState
      }
    },
    {
      questionName: 'custom-questions-intro',
      sectionName: PREFERENCES_SECTION,
      Component: QuizCustomQuestionInterstitial,
      shouldSkip: !customQuestionSteps.length || isBuyerAppAuthRemovalEnabled,
      otherProps: {
        breederName
      }
    },
    ...customQuestionSteps,
    {
      questionName: 'zip',
      questionTitle: 'Zipcode',
      sectionName: HOME_SECTION,
      Component: QuizZipcodeQuestion,
      shouldSkip:
        !isBuyerAppAuthRemovalEnabled ||
        (isBuyerAppAuthRemovalEnabled && alreadyHasZipCode),
      isValid:
        Boolean(
          get(
            formState,
            'buyer_profile_attributes.address_attributes.postal_code'
          )
        ) &&
        !formErrors['buyer_profile_attributes.address_attributes.postal_code']
    },
    {
      questionName: 'profile-photo',
      questionTitle: 'Picture',
      sectionName: PREFERENCES_SECTION,
      Component: QuizProfilePhotoQuestion,
      onChange: addToFormState,
      otherProps: {
        handleAllUploadSuccess,
        handleAllUploadError,
        allUploadingUploads,
        handleUploadInputChange,
        programName
      },
      isAnswered: Boolean(
        formState.buyer_profile_attributes.user_attributes
          .profile_photo_attributes.id
      ),
      shouldSkip: alreadyHasProfilePhoto
    },
    {
      questionName: 'phone-number',
      questionTitle: "What's your phone number?",
      sectionName: PREFERENCES_SECTION,
      Component: QuizPhoneNumberQuestion,
      isAnswered: alreadyHasPhoneNumber,
      shouldSkip: alreadyHasPhoneNumber,
      otherProps: {
        breederName,
        addToFormState
      }
    },
    {
      questionName: 'over-eighteen',
      questionTitle: 'Age',
      sectionName: HOME_SECTION,
      Component: QuizOverEighteenQuestion,
      shouldSkip:
        !isBuyerAppAuthRemovalEnabled ||
        (isBuyerAppAuthRemovalEnabled && alreadyOverEighteen),
      isValid:
        typeof normalizeFormValue(formState.is_over_eighteen) === 'boolean'
    },
    {
      questionName: 'prefers-good-dog-pay',
      questionTitle: 'Payment',
      sectionName: PREFERENCES_SECTION,
      Component: QuizGoodDogPayPreferenceQuestion,
      otherProps: {
        onSubmit: (alreadyClaimedAccount && onSubmit) || null
      }
    },
    {
      questionName: 'password',
      questionTitle: 'Password',
      sectionName: PREFERENCES_SECTION,
      Component: QuizPasswordQuestion,
      shouldSkip:
        !isBuyerAppAuthRemovalEnabled ||
        (isBuyerAppAuthRemovalEnabled && alreadyClaimedAccount),
      isValid:
        Boolean(formState.buyer_profile_attributes.user_attributes.password) &&
        !formErrors['buyer_profile_attributes.user_attributes.password'],
      otherProps: {
        onSubmit
      }
    }
  ].filter(s => !s.shouldSkip);
}

export default function QuizContainer(props) {
  // This is a hack to prevent tests from breaking on photo upload
  const {
    is_over_eighteen: isOverEighteen,
    buyer_profile_attributes: {
      user_attributes: userAttributes,
      address_attributes: addressAttributes
    }
  } = props.buyerApplicationData.value.buyer_application;

  const alreadyHasProfilePhoto = useMemo(() => {
    return Boolean(userAttributes?.profile_photo_attributes?.id);
  }, [userAttributes.id]);

  const alreadyHasPhoneNumber = useMemo(() => {
    return Boolean(userAttributes?.phone_number);
  }, [userAttributes.id]);

  const alreadyClaimedAccount = useMemo(() => {
    return Boolean(userAttributes?.claimed);
  }, [userAttributes.id]);

  const alreadyHasEmail = useMemo(() => {
    return Boolean(userAttributes?.email);
  }, [userAttributes.id]);

  const alreadyHasZipCode = useMemo(() => {
    return Boolean(addressAttributes?.postal_code);
  }, [userAttributes.id]);

  const alreadyOverEighteen = useMemo(() => {
    return Boolean(isOverEighteen);
  }, [userAttributes.id]);

  const orderedSteps = mapPropsToOrderedSteps({
    ...props,
    alreadyHasProfilePhoto,
    alreadyHasPhoneNumber,
    alreadyClaimedAccount,
    alreadyHasEmail,
    alreadyHasZipCode,
    alreadyOverEighteen
  });

  let index = orderedSteps.findIndex(
    s => s.isValid === false || s.isAnswered === false
  );

  if (index === -1) {
    index = orderedSteps.length - 1;
  }

  /*
    This passes in the onSubmit prop to the last question in the quiz.
    The onSubmit prop should also be added to the potential last question components
    to be passed into FullPageQuizQuestion as well. Ex: QuizDeliveryMethodQuestion.
  */
  orderedSteps[orderedSteps.length - 1].otherProps = {
    ...orderedSteps[orderedSteps.length - 1].otherProps,
    onSubmit: props.onSubmit
  };

  return <Quiz {...props} index={index} orderedSteps={orderedSteps} />;
}

QuizContainer.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  buyerApplicationData: PropTypes.object.isRequired
};
